import React, { useState, useEffect } from 'react';
import { Send, AlertCircle, Loader2, AlertTriangle, CheckCircle, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@vaiu/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
// Removed AlertDialog import - using inline confirmation instead
import { Badge } from '@vaiu/ui/badge';

interface EthereumWithdrawFlowEnhancedProps {
  balance: {
    balanceEth: number;
    balanceUsd: number;
    ethPrice: number;
  } | null;
}

const EthereumWithdrawFlowEnhanced: React.FC<EthereumWithdrawFlowEnhancedProps> = ({ balance }) => {
  const { toast } = useToast();
  const [amount, setAmount] = useState('');
  const [toAddress, setToAddress] = useState('');
  const [resolvedAddress, setResolvedAddress] = useState('');
  const [ensName, setEnsName] = useState('');
  const [gasEstimate, setGasEstimate] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [gasWarning, setGasWarning] = useState<'high' | 'very-high' | null>(null);

  // Thresholds for warnings
  const LARGE_AMOUNT_USD = 1000; // Require confirmation for amounts over $1000
  const HIGH_GAS_GWEI = 50; // Warn if gas is over 50 Gwei
  const VERY_HIGH_GAS_GWEI = 100; // Strong warning if gas is over 100 Gwei

  // Check if input is ENS name
  const isENSName = (input: string) => {
    return input.endsWith('.eth') || input.endsWith('.xyz') || input.endsWith('.app');
  };

  // Handle address/ENS input change
  const handleAddressChange = async (value: string) => {
    setToAddress(value);
    setResolvedAddress('');
    setEnsName('');

    if (isENSName(value) && value.length > 4) {
      setResolving(true);
      try {
        const result = await platformApi.ethereum.resolveENSName(value);
        if (result.isValid) {
          setResolvedAddress(result.address);
          setEnsName(value);
          toast({
            title: 'ENS Resolved',
            description: `${value} → ${result.address.slice(0, 6)}...${result.address.slice(-4)}`,
          });
        } else {
          toast({
            title: 'ENS Not Found',
            description: `Could not resolve ${value}`,
            variant: 'destructive',
          });
        }
      } catch (error) {
        console.error('ENS resolution failed:', error);
      } finally {
        setResolving(false);
      }
    } else if (value.startsWith('0x') && value.length === 42) {
      // Valid Ethereum address, check for reverse ENS
      try {
        const result = await platformApi.ethereum.lookupENSName(value);
        if (result.hasName && result.ensName) {
          setEnsName(result.ensName);
        }
      } catch (error) {
        console.error('ENS reverse lookup failed:', error);
      }
    }
  };

  const handleEstimateGas = async () => {
    const targetAddress = resolvedAddress || toAddress;
    if (!amount || !targetAddress) return;

    setEstimating(true);
    try {
      const estimate = await platformApi.ethereum.estimateGas(parseFloat(amount), targetAddress);
      setGasEstimate(estimate);

      // Check gas prices and set warnings
      const gasPriceGwei = parseInt(estimate.gasPrice) / 1e9;
      if (gasPriceGwei > VERY_HIGH_GAS_GWEI) {
        setGasWarning('very-high');
      } else if (gasPriceGwei > HIGH_GAS_GWEI) {
        setGasWarning('high');
      } else {
        setGasWarning(null);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to estimate gas',
        variant: 'destructive',
      });
    } finally {
      setEstimating(false);
    }
  };

  const handleWithdrawClick = () => {
    const amountUsd = parseFloat(amount) * (balance?.ethPrice || 0);
    
    // Check if amount is large and requires confirmation
    if (amountUsd > LARGE_AMOUNT_USD) {
      setShowConfirmDialog(true);
    } else {
      handleWithdraw();
    }
  };

  const handleWithdraw = async () => {
    const targetAddress = resolvedAddress || toAddress;
    if (!amount || !targetAddress) return;

    setLoading(true);
    setShowConfirmDialog(false);
    
    try {
      const result = await platformApi.ethereum.withdraw(parseFloat(amount), targetAddress);
      
      toast({
        title: 'Withdrawal Successful',
        description: `Transaction hash: ${result.txHash.slice(0, 10)}...`,
      });

      // Reset form
      setAmount('');
      setToAddress('');
      setResolvedAddress('');
      setEnsName('');
      setGasEstimate(null);
      setGasWarning(null);

      // Open explorer in new tab
      if (result.explorerUrl) {
        window.open(result.explorerUrl, '_blank');
      }
    } catch (error: any) {
      toast({
        title: 'Withdrawal Failed',
        description: error.message || 'Failed to withdraw ETH',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const maxAmount = balance?.balanceEth || 0;
  const amountUsd = parseFloat(amount || '0') * (balance?.ethPrice || 0);
  const targetAddress = resolvedAddress || toAddress;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Withdraw ETH</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Available Balance</Label>
            <div className="text-2xl font-bold">
              {balance?.balanceEth?.toFixed(6) || '0.000000'} ETH
            </div>
            <p className="text-sm text-muted-foreground">
              ≈ ${balance?.balanceUsd?.toFixed(2) || '0.00'} USD
            </p>
          </div>

          <div>
            <Label htmlFor="to-address">
              Destination Address or ENS Name
            </Label>
            <div className="relative">
              <Input
                id="to-address"
                placeholder="0x... or name.eth"
                value={toAddress}
                onChange={(e) => handleAddressChange(e.target.value)}
                className={resolving ? 'pr-10' : ''}
              />
              {resolving && (
                <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin" />
              )}
            </div>
            
            {/* Show resolved address */}
            {resolvedAddress && (
              <div className="mt-2 p-2 bg-muted rounded-md">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="text-sm">
                    Resolved to: <code className="font-mono">{resolvedAddress.slice(0, 8)}...{resolvedAddress.slice(-6)}</code>
                  </span>
                </div>
              </div>
            )}

            {/* Show ENS name if found */}
            {ensName && !isENSName(toAddress) && (
              <div className="mt-2 flex items-center space-x-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  ENS: {ensName}
                </span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="withdraw-amount">Amount (ETH)</Label>
            <div className="flex space-x-2">
              <Input
                id="withdraw-amount"
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                min="0.001"
                max={maxAmount}
                step="0.001"
              />
              <Button
                variant="outline"
                onClick={() => setAmount(maxAmount.toString())}
                disabled={maxAmount <= 0}
              >
                MAX
              </Button>
            </div>
            {amount && (
              <div className="mt-1 space-y-1">
                <p className="text-sm text-muted-foreground">
                  ≈ ${amountUsd.toFixed(2)} USD
                </p>
                {amountUsd > LARGE_AMOUNT_USD && (
                  <Badge variant="outline" className="text-yellow-600">
                    Large transaction - confirmation required
                  </Badge>
                )}
              </div>
            )}
          </div>

          {amount && targetAddress && !gasEstimate && (
            <Button
              variant="outline"
              className="w-full"
              onClick={handleEstimateGas}
              disabled={estimating || resolving}
            >
              {estimating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Estimating Fees...
                </>
              ) : (
                'Estimate Fees'
              )}
            </Button>
          )}

          {/* Gas Warning */}
          {gasWarning && (
            <Alert variant={gasWarning === 'very-high' ? 'destructive' : 'default'}>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>
                {gasWarning === 'very-high' ? 'Very High' : 'High'} Gas Prices Detected
              </AlertTitle>
              <AlertDescription>
                Network gas prices are {gasWarning === 'very-high' ? 'extremely' : ''} high right now.
                Consider waiting for lower gas prices unless this transaction is urgent.
                Current: {gasEstimate && (parseInt(gasEstimate.gasPrice) / 1e9).toFixed(0)} Gwei
              </AlertDescription>
            </Alert>
          )}

          {gasEstimate && (
            <Alert>
              <AlertDescription>
                <strong>Fee Breakdown:</strong>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Network Fee: {gasEstimate.gasCostEth?.toFixed(6)} ETH (${gasEstimate.gasCostUsd?.toFixed(2)})</li>
                  <li>Platform Fee (1.5%): {(parseFloat(amount) * 0.015).toFixed(6)} ETH</li>
                  <li>Total Cost: ${((parseFloat(amount) + parseFloat(amount) * 0.015) * (balance?.ethPrice || 0) + gasEstimate.gasCostUsd).toFixed(2)}</li>
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <Button
            className="w-full"
            onClick={handleWithdrawClick}
            disabled={
              loading || 
              !amount || 
              !targetAddress || 
              parseFloat(amount) > maxAmount ||
              resolving
            }
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing Withdrawal...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Withdraw {amount || '0'} ETH
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Inline Confirmation for Large Amounts */}
      {showConfirmDialog && (
        <Card className="mt-4 border-orange-200 bg-orange-50">
          <CardHeader>
            <CardTitle className="text-orange-900">Confirm Large Transaction</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p>You are about to withdraw a large amount:</p>
            <div className="bg-white p-3 rounded-md space-y-2 border">
              <div className="flex justify-between">
                <span>Amount:</span>
                <strong>{amount} ETH</strong>
              </div>
              <div className="flex justify-between">
                <span>Value:</span>
                <strong>${amountUsd.toFixed(2)} USD</strong>
              </div>
              <div className="flex justify-between">
                <span>To:</span>
                <code className="font-mono text-sm">
                  {ensName || `${targetAddress.slice(0, 6)}...${targetAddress.slice(-4)}`}
                </code>
              </div>
            </div>
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Please double-check the recipient address. Ethereum transactions cannot be reversed.
              </AlertDescription>
            </Alert>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowConfirmDialog(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleWithdraw}
              >
                Confirm Withdrawal
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default EthereumWithdrawFlowEnhanced;
